import DOMPurify from 'dompurify';

/**
 * Configuração DOMPurify idêntica à do backend (sanitize-html).
 * Deve ser mantida em sincronia com:
 * - Backend/vendepay-backend/src/common/utils/html-sanitizer.util.ts
 * - Frontend/vendepay-frontend/src/common/utils/html-sanitizer.util.ts
 *
 * Tags permitidas: p, br, div, span, strong, b, em, i, u, s, h1-h6,
 *                  ul, ol, li, blockquote, a, img
 *
 * CSS inline: allowlist de propriedades; valores com url()/expression()/javascript: são removidos.
 * Bloqueado: script, style (tag), iframe, object, embed, form, input, button,
 *            meta, link, base, noscript, template, svg — e qualquer on*.
 */
const PURIFY_CONFIG: DOMPurify.Config = {
  ALLOWED_TAGS: [
    'p', 'br', 'div', 'span',
    'strong', 'b', 'em', 'i', 'u', 's',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'blockquote',
    'a', 'img',
  ],
  ALLOWED_ATTR: [
    'class', 'style',
    'href', 'target', 'rel',
    'src', 'alt', 'width', 'height',
  ],
  // Permite o atributo target (DOMPurify bloqueia por padrão)
  ADD_ATTR: ['target'],
  FORCE_BODY: true,
};

/**
 * Propriedades CSS permitidas em style inline.
 *
 * Bloqueadas intencionalmente (não estão nesta lista):
 * - `background`         → shorthand permite url()
 * - `background-image`   → url()
 * - `position`           → fixed/absolute → overlay attacks
 * - `z-index`, `top`, `left`, `right`, `bottom` → layering/repositioning
 * - `transform`          → manipulação de layout
 * - `content`            → injeção de conteúdo via CSS pseudo-elementos
 * - `filter`, `clip-path`, `mask` → podem referenciar SVGs externos
 */
const ALLOWED_CSS_PROPS = new Set([
  'color', 'background-color',
  'font-size', 'font-weight', 'font-family', 'font-style', 'font-variant',
  'text-align', 'text-decoration', 'text-decoration-line', 'text-transform', 'text-indent',
  'line-height', 'letter-spacing', 'word-spacing', 'white-space', 'word-break',
  'margin', 'margin-top', 'margin-right', 'margin-bottom', 'margin-left',
  'padding', 'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
  'width', 'height', 'max-width', 'max-height', 'min-width', 'min-height',
  'border', 'border-top', 'border-right', 'border-bottom', 'border-left',
  'border-color', 'border-width', 'border-style', 'border-radius',
  'outline', 'outline-color', 'outline-width', 'outline-style',
  'display', 'float', 'clear', 'vertical-align',
  'text-shadow', 'box-shadow',
  'opacity',
]);

/** Padrão que bloqueia valores CSS com vetores de ataque. */
const DANGEROUS_CSS_VALUE = /url\s*\(|expression\s*\(|javascript\s*:/i;

/**
 * Remove propriedades CSS não permitidas e valores perigosos de uma string style inline.
 */
function filterInlineStyle(styleStr: string): string {
  return styleStr
    .split(';')
    .map((decl) => decl.trim())
    .filter((decl) => {
      if (!decl) return false;
      const colonIdx = decl.indexOf(':');
      if (colonIdx === -1) return false;
      const prop = decl.slice(0, colonIdx).trim().toLowerCase();
      const value = decl.slice(colonIdx + 1).trim();
      return ALLOWED_CSS_PROPS.has(prop) && !DANGEROUS_CSS_VALUE.test(value);
    })
    .join('; ');
}

// Hooks de segurança: executados uma única vez no carregamento do módulo
if (typeof window !== 'undefined') {
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    // <a>: força rel e bloqueia href não-http(s)
    if (node.tagName === 'A') {
      node.setAttribute('rel', 'noopener noreferrer');
      if (node.hasAttribute('target')) {
        node.setAttribute('target', '_blank');
      }
      const href = node.getAttribute('href') ?? '';
      if (href && !/^https?:\/\//i.test(href)) {
        node.removeAttribute('href');
      }
    }

    // <img>: permite apenas src HTTPS
    if (node.tagName === 'IMG') {
      const src = node.getAttribute('src') ?? '';
      if (src && !/^https:\/\//i.test(src)) {
        node.removeAttribute('src');
      }
    }

    // Qualquer tag: filtra o style inline pela allowlist de propriedades
    if (node.hasAttribute('style')) {
      const filtered = filterInlineStyle(node.getAttribute('style') ?? '');
      if (filtered) {
        node.setAttribute('style', filtered);
      } else {
        node.removeAttribute('style');
      }
    }
  });
}

/**
 * Sanitiza HTML arbitrário no cliente usando DOMPurify.
 * O backend também sanitiza com a mesma allowlist antes de persistir no banco.
 * Esta sanitização é defense-in-depth para proteger contra divergências de parser.
 */
export function sanitizeCustomHtml(raw: string): string {
  if (!raw) return '';
  return DOMPurify.sanitize(raw, PURIFY_CONFIG) as string;
}
